import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Build as BuildIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { addDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import turnoService, {
  LABEL_ESTADO_TURNO,
  TurnoReadDto,
} from '../../services/turnoService';
import { facturaPosPath, ROUTES } from '../../config/routes';
import {
  BORDER_ESTADO_TURNO,
  COLOR_ESTADO_TURNO,
  formatRangoHorario,
  gridSlots,
  GRID_HORA_FIN,
  GRID_HORA_INICIO,
  GRID_ROW_PX,
  GRID_VIEWPORT_MAX_PX,
  labelSlotHora,
  layoutTurnosGrilla,
  minutosAHora,
  parseIsoDate,
  shouldShowSlotLabel,
  toIsoDate,
} from '../../utils/turnoHorario';

const clienteLabel = (t: TurnoReadDto): string =>
  (t.nombreCliente || (t.nroCliente ? String(t.nroCliente) : 'Sin cliente')).trim();

const servicioLabel = (t: TurnoReadDto): string =>
  (t.descripcionServicio || t.codigoServicio || '—').trim();

const turnoEstaFacturado = (t: TurnoReadDto): boolean => t.estado === 'FACTURADO';

const OrganizadorServicios: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [fecha, setFecha] = useState<Date>(() => parseIsoDate(toIsoDate(new Date())));
  const [turnos, setTurnos] = useState<TurnoReadDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [generandoOt, setGenerandoOt] = useState(false);

  const fechaIso = useMemo(() => toIsoDate(fecha), [fecha]);
  const slots = useMemo(() => gridSlots(), []);
  const gridHeight = slots.length * GRID_ROW_PX;

  const selectedTurno = useMemo(
    () => turnos.find((t) => String(t.id) === String(selectedId)) ?? null,
    [turnos, selectedId],
  );

  const turnosLayout = useMemo(() => layoutTurnosGrilla(turnos), [turnos]);

  const fetchTurnosDia = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const data = await turnoService.getTurnos({
        fecha: fechaIso,
        pageNumber: 1,
        pageSize: 500,
        signal,
      });
      const activos = data.items.filter((t) => !t.anulado);
      setTurnos(activos);
      setSelectedId((prev) => {
        if (prev != null && activos.some((t) => String(t.id) === String(prev))) return prev;
        return activos[0]?.id ?? null;
      });
    } catch (error: unknown) {
      if (signal?.aborted || (error as { code?: string })?.code === 'ERR_CANCELED') return;
      enqueueSnackbar('Error al cargar turnos del día', { variant: 'error' });
      setTurnos([]);
      setSelectedId(null);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [fechaIso, enqueueSnackbar]);

  useEffect(() => {
    const controller = new AbortController();
    void fetchTurnosDia(controller.signal);
    return () => controller.abort();
  }, [fetchTurnosDia]);

  const patchTurnoLocal = useCallback((updated: TurnoReadDto) => {
    setTurnos((prev) => prev.map((t) => (String(t.id) === String(updated.id) ? { ...t, ...updated } : t)));
  }, []);

  const handleGenerarOt = useCallback(
    async (turno: TurnoReadDto) => {
      if (turnoEstaFacturado(turno)) {
        enqueueSnackbar('El turno ya está facturado; no se puede generar una orden de trabajo', {
          variant: 'warning',
        });
        return;
      }
      if (!turno.nroCliente || turno.nroCliente <= 0) {
        enqueueSnackbar('El turno debe tener un cliente habitual', { variant: 'warning' });
        return;
      }
      try {
        setGenerandoOt(true);
        const updated = await turnoService.generarOrdenTrabajo(turno.id);
        patchTurnoLocal(updated);
        enqueueSnackbar('Orden de trabajo generada', { variant: 'success' });
        if (updated.nroFormulario) {
          navigate(ROUTES.servicio.ordenesTrabajo.editar(updated.nroFormulario));
        }
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        enqueueSnackbar(
          err?.response?.data?.message || err?.message || 'Error al generar la orden de trabajo',
          { variant: 'error' },
        );
      } finally {
        setGenerandoOt(false);
      }
    },
    [enqueueSnackbar, navigate, patchTurnoLocal],
  );

  const handleRegistrarSena = useCallback(
    (turno: TurnoReadDto) => {
      if (turnoEstaFacturado(turno)) {
        enqueueSnackbar('El turno ya está facturado; no se puede registrar seña', {
          variant: 'warning',
        });
        return;
      }
      if (!turno.nroCliente || turno.nroCliente <= 0) {
        enqueueSnackbar('El turno debe tener un cliente habitual para registrar la seña', {
          variant: 'warning',
        });
        return;
      }
      const detalle = [
        'Seña servicio',
        `turno #${turno.id}`,
        turno.nroFormulario ? `OT #${turno.nroFormulario}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
      navigate(ROUTES.tesoreria.recibos.nuevoConCliente(turno.nroCliente, { detalle }));
    },
    [enqueueSnackbar, navigate],
  );

  const turnoCardSx = (t: TurnoReadDto, selected: boolean) => ({
    p: 1.25,
    borderRadius: 1,
    cursor: 'pointer',
    border: '2px solid',
    borderColor: selected ? '#1565c0' : BORDER_ESTADO_TURNO[t.estado],
    bgcolor: COLOR_ESTADO_TURNO[t.estado],
    opacity: t.anulado ? 0.5 : 1,
    boxShadow: selected ? '0 0 0 2px rgba(21,101,192,0.35)' : 'none',
    '&:hover': { filter: 'brightness(0.97)' },
  });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Organizador de servicios
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/servicio/turnos/nuevo')}
            >
              Agregar turno
            </Button>
          </Stack>

          <Grid container spacing={2}>
            {/* Panel izquierdo: calendario + turnos del día */}
            <Grid item xs={12} md={4} lg={3}>
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton size="small" onClick={() => setFecha((d) => addDays(d, -1))} aria-label="Día anterior">
                      <ChevronLeftIcon />
                    </IconButton>
                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      value={fechaIso}
                      onChange={(e) => {
                        if (e.target.value) setFecha(parseIsoDate(e.target.value));
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <IconButton size="small" onClick={() => setFecha((d) => addDays(d, 1))} aria-label="Día siguiente">
                      <ChevronRightIcon />
                    </IconButton>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    {fecha.toLocaleDateString('es-AR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </Typography>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Clientes del día
                  </Typography>
                  {loading ? (
                    <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : turnos.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No hay turnos para esta fecha
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {turnos.map((t) => {
                        const selected = String(t.id) === String(selectedId);
                        return (
                          <Box
                            key={String(t.id)}
                            onClick={() => setSelectedId(t.id)}
                            onDoubleClick={() => navigate(`/servicio/turnos/${encodeURIComponent(String(t.id))}`)}
                            sx={turnoCardSx(t, selected)}
                          >
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                              <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1 }}>
                                {clienteLabel(t)} - {servicioLabel(t)}
                              </Typography>
                              {t.nroFormulario ? (
                                <Chip size="small" label={`OT ${t.nroFormulario}`} sx={{ height: 20, fontSize: '0.65rem' }} />
                              ) : null}
                            </Stack>
                            <Typography variant="caption" display="block">
                              {formatRangoHorario(t.horaInicio, t.duracionMinutos)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Paper>

                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 16, height: 16, bgcolor: COLOR_ESTADO_TURNO.FACTURADO, borderRadius: 0.5 }} />
                    <Typography variant="caption">Facturado</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 16, height: 16, bgcolor: COLOR_ESTADO_TURNO.RECIBIDO, borderRadius: 0.5 }} />
                    <Typography variant="caption">Recibido</Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Grid>

            {/* Panel derecho: grilla horaria + detalle */}
            <Grid item xs={12} md={8} lg={9}>
              <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Lista de servicios
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {fecha.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  {' · '}
                  {GRID_HORA_INICIO}:00 a {GRID_HORA_FIN}:00
                </Typography>

                {loading ? (
                  <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: GRID_VIEWPORT_MAX_PX,
                      bgcolor: '#fafafa',
                    }}
                  >
                    <Box sx={{ width: 64, flexShrink: 0, bgcolor: '#f0f0f0', borderRight: '1px solid', borderColor: 'divider' }}>
                      {slots.map((min) => (
                        <Box
                          key={min}
                          sx={{
                            height: GRID_ROW_PX,
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            pt: 0.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {shouldShowSlotLabel(min) ? labelSlotHora(min) : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ flex: 1, position: 'relative', minHeight: gridHeight, bgcolor: '#fff' }}>
                      {slots.map((min, idx) => (
                        <Box
                          key={min}
                          onDoubleClick={() =>
                            navigate('/servicio/turnos/nuevo', {
                              state: { fecha: fechaIso, horaInicio: minutosAHora(min) },
                            })
                          }
                          sx={{
                            height: GRID_ROW_PX,
                            borderBottom: '1px solid',
                            borderColor: idx % 2 === 0 ? 'grey.200' : 'grey.100',
                            bgcolor: idx % 2 === 0 ? '#fff' : '#f9f9f9',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: idx % 2 === 0 ? '#f5f9ff' : '#eef5ff' },
                          }}
                        />
                      ))}

                      {turnos.map((t) => {
                        const selected = String(t.id) === String(selectedId);
                        const layout = turnosLayout.get(String(t.id));
                        if (!layout) return null;
                        if (layout.top + layout.height > gridHeight + GRID_ROW_PX) return null;

                        const gap = 3;
                        const widthPct = 100 / layout.columns;
                        const leftPct = layout.column * widthPct;

                        return (
                          <Box
                            key={String(t.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedId(t.id);
                            }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              navigate(`/servicio/turnos/${encodeURIComponent(String(t.id))}`);
                            }}
                            sx={{
                              position: 'absolute',
                              top: layout.top,
                              height: layout.height,
                              left: `calc(${leftPct}% + ${gap}px)`,
                              width: `calc(${widthPct}% - ${gap * 2}px)`,
                              px: 0.75,
                              py: 0.35,
                              borderRadius: 1,
                              cursor: 'pointer',
                              overflow: 'hidden',
                              border: '2px solid',
                              borderColor: selected ? '#1565c0' : BORDER_ESTADO_TURNO[t.estado],
                              bgcolor: COLOR_ESTADO_TURNO[t.estado],
                              boxShadow: selected ? 3 : 1,
                              zIndex: selected ? 2 : 1,
                              '&:hover': { filter: 'brightness(0.98)' },
                            }}
                          >
                            <Typography variant="caption" fontWeight={700} display="block" noWrap sx={{ lineHeight: 1.2 }}>
                              {clienteLabel(t)}
                              {t.nroFormulario ? ` · OT ${t.nroFormulario}` : ''}
                            </Typography>
                            {layout.height >= 40 ? (
                              <>
                                <Typography variant="caption" display="block" noWrap sx={{ lineHeight: 1.2 }}>
                                  {servicioLabel(t)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ lineHeight: 1.2 }}>
                                  {formatRangoHorario(t.horaInicio, t.duracionMinutos)}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ lineHeight: 1.2 }}>
                                {formatRangoHorario(t.horaInicio, t.duracionMinutos)}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {selectedTurno && (
                  <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: '#f8fbff' }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Detalle del turno
                        </Typography>
                        {selectedTurno.nroFormulario ? (
                          <Chip size="small" color="primary" label={`OT #${selectedTurno.nroFormulario}`} />
                        ) : null}
                      </Stack>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        disabled={turnoEstaFacturado(selectedTurno)}
                        onClick={() =>
                          navigate(`/servicio/turnos/${encodeURIComponent(String(selectedTurno.id))}`)
                        }
                      >
                        Editar
                      </Button>
                    </Stack>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Cliente:</strong> {clienteLabel(selectedTurno)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Servicio:</strong> {servicioLabel(selectedTurno)}
                        </Typography>
                        {selectedTurno.patente ? (
                          <Typography variant="body2">
                            <strong>Unidad:</strong> {selectedTurno.patente}
                          </Typography>
                        ) : null}
                        <Typography variant="body2">
                          <strong>Horario:</strong>{' '}
                          {formatRangoHorario(selectedTurno.horaInicio, selectedTurno.duracionMinutos)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="body2" component="span">
                            <strong>Estado:</strong>
                          </Typography>
                          <Chip
                            size="small"
                            label={LABEL_ESTADO_TURNO[selectedTurno.estado]}
                            sx={{ bgcolor: COLOR_ESTADO_TURNO[selectedTurno.estado] }}
                          />
                        </Stack>
                        {selectedTurno.telefonoCliente ? (
                          <Typography variant="body2">
                            <strong>Teléfono:</strong> {selectedTurno.telefonoCliente}
                          </Typography>
                        ) : null}
                        {selectedTurno.nota ? (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>Nota:</strong> {selectedTurno.nota}
                          </Typography>
                        ) : null}
                      </Grid>
                    </Grid>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 2 }}
                    >
                      {selectedTurno.nroFormulario ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<BuildIcon />}
                          onClick={() =>
                            navigate(ROUTES.servicio.ordenesTrabajo.editar(selectedTurno.nroFormulario!))
                          }
                        >
                          Abrir OT
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<BuildIcon />}
                          disabled={
                            generandoOt ||
                            !selectedTurno.nroCliente ||
                            turnoEstaFacturado(selectedTurno)
                          }
                          onClick={() => void handleGenerarOt(selectedTurno)}
                        >
                          {generandoOt ? 'Generando…' : 'Generar orden de trabajo'}
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PaymentIcon />}
                        disabled={!selectedTurno.nroCliente || turnoEstaFacturado(selectedTurno)}
                        onClick={() => handleRegistrarSena(selectedTurno)}
                      >
                        Registrar seña
                      </Button>
                      <Can permiso="ventas.facturas.nuevo">
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<ReceiptLongIcon />}
                        disabled={!selectedTurno.nroFormulario || turnoEstaFacturado(selectedTurno)}
                        onClick={() =>
                          navigate(facturaPosPath({ ordenTrabajoId: selectedTurno.nroFormulario! }))
                        }
                      >
                        Facturar
                      </Button>
                      </Can>
                    </Stack>
                  </Paper>
                )}
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrganizadorServicios;
